// theme.ts — accent-color theme presets (Einstellungen "Darstellung"). Each preset is a
// {accent, accentStrong} pair applied as CSS custom properties on :root, persisted in
// AppSettings (settings.ts) under `accentPreset`. This app is dark-only (global.css sets
// color-scheme: dark) — no light-mode variant needed, only the accent hue changes.
//
// The boot-time "no flash" application lives in index.html as a small inline, non-module
// <script> that duplicates this file's color values (see that script's own comment) —
// it must run synchronously before Preact (and this module) even loads, so it can't
// import ES modules. Keep the two in sync by hand whenever a preset here changes.
export type AccentPreset = 'blau' | 'gruen' | 'bernstein' | 'violett';

export interface AccentPresetDef {
  key: AccentPreset;
  label: string;
  accent: string;
  accentStrong: string;
}

export const ACCENT_PRESETS: Record<AccentPreset, AccentPresetDef> = {
  blau: { key: 'blau', label: 'Blau', accent: '#60a5fa', accentStrong: '#3b82f6' },
  gruen: { key: 'gruen', label: 'Grün', accent: '#4ade80', accentStrong: '#22c55e' },
  bernstein: { key: 'bernstein', label: 'Bernstein', accent: '#fbbf24', accentStrong: '#f59e0b' },
  violett: { key: 'violett', label: 'Violett', accent: '#7c93ff', accentStrong: '#5b76f7' },
};

export const ACCENT_PRESET_ORDER: AccentPreset[] = ['blau', 'gruen', 'bernstein', 'violett'];

export const DEFAULT_ACCENT_PRESET: AccentPreset = 'blau';

export function isAccentPreset(value: unknown): value is AccentPreset {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(ACCENT_PRESETS, value);
}

/** Applies a preset's colors as CSS custom properties on :root — inline style (element.style)
 * wins the cascade over global.css's `:root { --accent: ... }` rule regardless of load
 * order, which is also why index.html's pre-paint boot script (duplicated there, see this
 * file's header comment) uses the same technique to avoid a flash of the previous/default
 * accent on load. */
export function applyAccentPreset(preset: AccentPreset): void {
  if (typeof document === 'undefined') return;
  const def = ACCENT_PRESETS[preset] ?? ACCENT_PRESETS[DEFAULT_ACCENT_PRESET];
  document.documentElement.style.setProperty('--accent', def.accent);
  document.documentElement.style.setProperty('--accent-strong', def.accentStrong);
}

/** Reads a CSS custom property's current computed value off :root (e.g. '--accent') —
 * used by chart components so the primary series color follows whichever accent preset is
 * actually applied right now, instead of a hardcoded hex. Falls back when there's no DOM
 * (this module is also imported by node-environment unit tests) or the variable is unset. */
export function readCssVar(name: string, fallback: string): string {
  if (typeof document === 'undefined' || typeof getComputedStyle !== 'function') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

/** Fixed palette cycled for an arbitrary number of overlaid series — the Vergleich
 * multi-column overlay chart's per-scenario line colors, and any future "N distinct
 * series" chart. Index 0 = the current --accent theme preset (so the first/primary
 * scenario recolors with Einstellungen -> Darstellung, same as every other chart's
 * primary series), then the fixed --warn/--positive/--text-muted/--negative/--accent-strong
 * palette per the feature spec ("A=--accent, then --warn, --positive, --text-muted…"),
 * wrapping around (not erroring) for a 6th+ scenario. */
const SERIES_COLOR_VARS: { cssVar: string; fallback: string }[] = [
  { cssVar: '--accent', fallback: '#60a5fa' },
  { cssVar: '--warn', fallback: '#fbbf24' },
  { cssVar: '--positive', fallback: '#4ade80' },
  { cssVar: '--text-muted', fallback: '#93a0b8' },
  { cssVar: '--negative', fallback: '#f87171' },
  { cssVar: '--accent-strong', fallback: '#3b82f6' },
];

export function seriesColor(index: number): string {
  const entry = SERIES_COLOR_VARS[((index % SERIES_COLOR_VARS.length) + SERIES_COLOR_VARS.length) % SERIES_COLOR_VARS.length]!;
  return readCssVar(entry.cssVar, entry.fallback);
}

/** Parses a `#rrggbb` hex color into an `rgba(r, g, b, alpha)` string — used to derive a
 * chart's low-opacity fill from the current accent color without a CSS `color-mix()` (uPlot
 * series options take plain color strings, not CSS). Falls back to a fully-transparent
 * black on anything that isn't a well-formed 6-digit hex, rather than guessing. */
export function hexToRgba(hex: string, alpha: number): string {
  const match = /^#([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!match) return `rgba(0,0,0,${alpha})`;
  const int = Number.parseInt(match[1]!, 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}
